import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiRequest from '../../api/request';

/**
 * Landing CMS — the public parent-landing page's copy, imagery, meta and
 * tag-manager slot.
 *
 * ---------------------------------------------------------------------------
 * WHY EDITS ARE NOT INSTANT, AND WHY THE SCREEN SAYS SO
 *
 * The page is statically prerendered with ISR. That is what puts the meta tags
 * in the HTML the server sends — and it has to be there, because the card
 * scrapers at Facebook, LinkedIn and WhatsApp read raw HTML and never run
 * JavaScript. A title fetched in the browser would be invisible to all of them.
 *
 * The cost is a revalidation window. Every write returns `revalidateSeconds`
 * and the UI reports it, so an editor is told when their change goes live
 * instead of refreshing and assuming it failed.
 *
 * ---------------------------------------------------------------------------
 * `status` IS CHECKED BY HAND, EVERYWHERE
 *
 * api/request.js resolves on failure — its catch returns `err.response.data`
 * rather than rethrowing — so a rejected thunk never happens for a 400 and
 * `.unwrap()` would hand the component an error envelope as if it were data.
 *
 * That matters more here than usual: the server's rejections carry the SEO
 * guidance that is the point of the screen ("this canonical URL is relative",
 * "this is not a GTM container id"). Swallowing them leaves a Save button that
 * silently does nothing.
 */

const initialState = {
  page: null,
  sections: [],
  meta: null,
  metaPreview: null,
  settings: null,
  testimonials: [],
  options: { robots: [], twitterCards: [] },
  revalidateSeconds: 300,
  loading: false,
  saving: false,
  // Live SEO feedback for the meta form, refreshed as the editor types.
  seo: { valid: true, errors: [], warnings: [], preview: null, checking: false },
};

const BASE = 'student-portal/cms/landing';

const unwrapOrThrow = (res, rejectWithValue) => {
  if (res?.status === 'success') return res;
  return rejectWithValue({
    message: res?.message || 'Request failed',
    errors: res?.data?.errors || [],
    warnings: res?.data?.warnings || [],
  });
};

/* ------------------------------------------------------------------ */
/* Reads                                                               */
/* ------------------------------------------------------------------ */

export const getLandingCms = createAsyncThunk('landingCms/get', async (slug = 'parent-landing') => {
  const res = await apiRequest({ method: 'get', url: `${BASE}?slug=${encodeURIComponent(slug)}` });
  return res;
});

/* ------------------------------------------------------------------ */
/* Writes                                                              */
/* ------------------------------------------------------------------ */

/** Dry run: validation plus the Google-result preview. No writes. */
export const previewMeta = createAsyncThunk('landingCms/previewMeta', async (payload) => {
  const res = await apiRequest({ method: 'post', url: `${BASE}/meta/preview`, data: payload });
  return res;
});

export const updateMeta = createAsyncThunk(
  'landingCms/updateMeta',
  async (payload, { rejectWithValue }) => {
    const res = await apiRequest({ method: 'put', url: `${BASE}/meta`, data: payload });
    return unwrapOrThrow(res, rejectWithValue);
  }
);

export const updateSettings = createAsyncThunk(
  'landingCms/updateSettings',
  async (payload, { rejectWithValue }) => {
    const res = await apiRequest({ method: 'put', url: `${BASE}/settings`, data: payload });
    return unwrapOrThrow(res, rejectWithValue);
  }
);

/**
 * A section and all its blocks, together.
 *
 * Omit `blocks` to leave them untouched; send `[]` to delete them all. Those
 * are different intentions and the server keeps them distinct, so a copy-only
 * save cannot wipe a section's cards.
 */
export const updateSection = createAsyncThunk(
  'landingCms/updateSection',
  async ({ id, ...payload }, { rejectWithValue }) => {
    const res = await apiRequest({ method: 'put', url: `${BASE}/sections/${id}`, data: payload });
    return unwrapOrThrow(res, rejectWithValue);
  }
);

export const saveTestimonial = createAsyncThunk(
  'landingCms/saveTestimonial',
  async ({ id, ...payload }, { rejectWithValue }) => {
    const res = await apiRequest({
      method: id ? 'put' : 'post',
      url: id ? `${BASE}/testimonials/${id}` : `${BASE}/testimonials`,
      data: payload,
    });
    return unwrapOrThrow(res, rejectWithValue);
  }
);

export const deleteTestimonial = createAsyncThunk(
  'landingCms/deleteTestimonial',
  async (id, { rejectWithValue }) => {
    const res = await apiRequest({ method: 'delete', url: `${BASE}/testimonials/${id}` });
    return unwrapOrThrow(res, rejectWithValue);
  }
);

const landingCmsSlice = createSlice({
  name: 'landingCms',
  initialState,
  reducers: {
    clearSeoFeedback: (state) => {
      state.seo = { ...initialState.seo };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getLandingCms.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getLandingCms.fulfilled, (state, action) => {
      const data = action?.payload?.data || {};
      state.loading = false;
      state.page = data.page || null;
      state.sections = data.sections || [];
      state.meta = data.meta || null;
      state.metaPreview = data.metaPreview || null;
      state.settings = data.settings || null;
      state.testimonials = data.testimonials || [];
      state.options = data.options || initialState.options;
      state.revalidateSeconds = data.revalidateSeconds ?? 300;
    });
    builder.addCase(getLandingCms.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(previewMeta.pending, (state) => {
      state.seo.checking = true;
    });
    builder.addCase(previewMeta.fulfilled, (state, action) => {
      const data = action?.payload?.data || {};
      state.seo = {
        valid: data.valid !== false,
        errors: data.errors || [],
        warnings: data.warnings || [],
        preview: data.preview || null,
        checking: false,
      };
    });
    builder.addCase(previewMeta.rejected, (state) => {
      state.seo.checking = false;
    });

    builder.addMatcher(
      (action) => action.type.startsWith('landingCms/') && action.type.endsWith('/pending') && action.type !== getLandingCms.pending.type && action.type !== previewMeta.pending.type,
      (state) => {
        state.saving = true;
      }
    );
    builder.addMatcher(
      (action) =>
        action.type.startsWith('landingCms/') &&
        (action.type.endsWith('/fulfilled') || action.type.endsWith('/rejected')) &&
        !action.type.startsWith(getLandingCms.typePrefix) &&
        !action.type.startsWith(previewMeta.typePrefix),
      (state) => {
        state.saving = false;
      }
    );
  },
});

export const { clearSeoFeedback } = landingCmsSlice.actions;
export default landingCmsSlice.reducer;
